import React, { useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { CaretDown, Check, Plus } from '@phosphor-icons/react';

// Sélecteur avec création à la volée d'un nouveau libellé (persisté via onCreate).
// options: [{ value, label, Icon?, iconColor? }]
export function CreatableSelect({
  value,
  onChange,
  options,
  placeholder = 'Sélectionner',
  onCreate,
  required = false,
  testId,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [creating, setCreating] = useState(false);
  const selected = options.find(o => o.value === value);

  const norm = (s) => (s || '').trim().toLowerCase();
  const filtered = options.filter(o => norm(o.label).includes(norm(query)) || norm(o.value).includes(norm(query)));
  const exact = options.some(o => norm(o.label) === norm(query));
  const canCreate = query.trim().length >= 2 && !exact && onCreate;

  const pick = (v) => {
    onChange(v);
    setOpen(false);
    setQuery('');
  };

  const handleCreate = async () => {
    if (!canCreate || creating) return;
    setCreating(true);
    try {
      const tag = await onCreate(query.trim());
      if (tag) pick(tag.value);
    } finally {
      setCreating(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className="w-full justify-between border-[#E8E6E1] font-normal"
          data-testid={testId}
          aria-required={required}
        >
          {selected ? (
            <span className="flex items-center gap-2 min-w-0">
              {selected.Icon && <selected.Icon size={14} style={{ color: selected.iconColor }} className="flex-shrink-0" />}
              <span className="truncate">{selected.label}</span>
            </span>
          ) : (
            <span className="text-[#5E6D66]">{placeholder}</span>
          )}
          <CaretDown size={14} className="text-[#5E6D66] flex-shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[320px] p-2" align="start">
        <Input
          autoFocus
          placeholder="Rechercher ou créer…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && canCreate) { e.preventDefault(); handleCreate(); } }}
          className="mb-2"
          data-testid={`${testId}-search`}
        />
        <div className="max-h-52 overflow-y-auto space-y-0.5">
          {filtered.map(o => (
            <button
              key={o.value}
              type="button"
              onClick={() => pick(o.value)}
              className="w-full flex items-center gap-2 px-2 py-1.5 rounded text-sm text-left hover:bg-[#F9F8F5]"
              data-testid={`${testId}-opt-${o.value}`}
            >
              {o.Icon && <o.Icon size={14} style={{ color: o.iconColor }} />}
              <span className="flex-1 truncate">{o.label}</span>
              {o.value === value && <Check size={14} className="text-[#2D5A4C]" />}
            </button>
          ))}
          {filtered.length === 0 && !canCreate && (
            <p className="text-xs text-[#5E6D66] italic px-2 py-1">Aucun résultat</p>
          )}
          {canCreate && (
            <button
              type="button"
              onClick={handleCreate}
              disabled={creating}
              className="w-full flex items-center gap-2 px-2 py-1.5 rounded text-sm text-left text-[#2D5A4C] hover:bg-[#2D5A4C]/10 font-medium"
              data-testid={`${testId}-create`}
            >
              <Plus size={14} />
              {creating ? 'Création…' : `Créer « ${query.trim()} »`}
            </button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
